import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Button,
  EmptyState,
  FilterToolbar,
  ReportTable,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type ReportTableColumn,
  type ReportTablePaginationState,
  type ReportTableSort,
} from '@roger/ui-components';
import { ArrowLeft, Download } from 'lucide-react';
import { PermissionSlug } from '../constants/featureFlags';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import {
  exportPatientDiagnosesReportReportsPatientDiagnosesExportGet,
  getPatientDiagnosesReportClinicOptionsReportsPatientDiagnosesClinicOptionsGet,
  getPatientDiagnosesReportReportsPatientDiagnosesGet,
} from '../services/api/sdk.gen';
import type {
  HelperEstimatedComorbidityAdjustment,
  PatientDiagnosesReportResult,
  PatientDiagnosesReportRow,
  PatientDiagnosesSortBy,
  UnacceptablePrimaryFilter,
  SortOrder,
  PayorType,
} from '../services/api/types.gen';
import { downloadResponseAsFile } from '../utils/downloadFile';
import {
  formatEnumReason,
  formatReportEmptyValue,
  readChoiceParam,
  readPositiveIntegerParam,
  REPORT_PAGE_SIZE_OPTIONS,
} from '../utils/reportDisplay';
import {
  getBusinessErrorMessage,
  getUserFacingApiMessageAsync,
} from '../utils/apiErrorDetail';
import ReportsPermissionAlert from './ReportsPermissionAlert';
import { useReportsBackHref } from '../reporting/backHref';
import type { AxiosResponse } from 'axios';

const UNACCEPTABLE_FILTER_OPTIONS = ['yes', 'no', 'unknown'] as const;
const ALL_VALUE = '__all__';
type ClinicOption = { id: string; name: string };

const PAYOR_TYPE_OPTIONS: {
  value: PayorType | 'unclassified';
  label: string;
}[] = [
  { value: 'MEDICAID_MANAGED_CARE', label: 'Medicaid Managed Care' },
  { value: 'MEDICAID_TRADITIONAL', label: 'Medicaid Traditional' },
  { value: 'MEDICARE_ADVANTAGE', label: 'Medicare Advantage' },
  { value: 'MEDICARE_HMO', label: 'Medicare HMO' },
  { value: 'MEDICARE_TRADITIONAL', label: 'Medicare Traditional' },
  { value: 'NONE_NO_CHARGE', label: 'None / No Charge' },
  { value: 'OTHER_GOVERNMENT', label: 'Other Government' },
  { value: 'PRIVATE_MANAGED_CARE', label: 'Private Managed Care' },
  { value: 'PRIVATE', label: 'Private' },
  { value: 'SELF_PAY', label: 'Self Pay' },
  { value: 'TITLE_PROGRAMS', label: 'Title Programs' },
  { value: 'TRICARE', label: 'Tricare' },
  { value: 'UNKNOWN', label: 'Unknown' },
  { value: 'VA_PLAN', label: 'VA' },
  { value: 'WORKERS_COMP', label: 'Workers Comp' },
  { value: 'unclassified', label: 'Unclassified' },
];

const columns: ReportTableColumn<PatientDiagnosesReportRow>[] = [
  {
    id: 'clinic_name',
    header: 'Clinic',
    accessor: (row: PatientDiagnosesReportRow) =>
      formatReportEmptyValue(row.clinic_name),
    enableSorting: true,
    minWidth: 160,
  },
  {
    id: 'patient_first_name',
    header: 'Patient First',
    accessor: 'patient_first_name' as const,
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'patient_last_name',
    header: 'Patient Last',
    accessor: 'patient_last_name' as const,
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'date_of_birth',
    header: 'DOB',
    accessor: 'date_of_birth' as const,
    enableSorting: true,
    minWidth: 120,
  },
  {
    id: 'mrn',
    header: 'MRN',
    accessor: (row: PatientDiagnosesReportRow) =>
      formatReportEmptyValue(row.mrn),
    enableSorting: true,
    minWidth: 120,
  },
  {
    id: 'soc_date',
    header: 'SOC Date',
    accessor: 'soc_date' as const,
    enableSorting: true,
    minWidth: 120,
  },
  {
    id: 'episode_start_date',
    header: 'Episode Start',
    accessor: 'episode_start_date' as const,
    enableSorting: true,
    minWidth: 140,
  },
  {
    id: 'episode_end_date',
    header: 'Episode End',
    accessor: 'episode_end_date' as const,
    enableSorting: true,
    minWidth: 140,
  },
  {
    id: 'primary_diagnosis_code',
    header: 'Primary Diagnosis',
    accessor: (row: PatientDiagnosesReportRow) =>
      row.primary_diagnosis_code
        ? row.primary_diagnosis_code
        : `Unavailable — ${formatEnumReason(row.unacceptable_primary_reason)}`,
    enableSorting: true,
    minWidth: 190,
  },
  {
    id: 'primary_diagnosis_description',
    header: 'Diagnosis Description',
    accessor: (row: PatientDiagnosesReportRow) =>
      formatReportEmptyValue(row.primary_diagnosis_description),
    minWidth: 260,
  },
  {
    id: 'secondary_diagnoses',
    header: 'Secondary Diagnoses',
    accessor: (row: PatientDiagnosesReportRow) =>
      row.secondary_diagnoses.length
        ? row.secondary_diagnoses.map(dx => dx.code).join(', ')
        : '—',
    minWidth: 240,
  },
  {
    id: 'helper_estimated_pdgm_grouping',
    header: 'PDGM Grouping',
    accessor: (row: PatientDiagnosesReportRow) =>
      formatReportEmptyValue(row.helper_estimated_pdgm_grouping),
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'helper_estimated_comorbidity_adjustment',
    header: 'Comorbidity',
    accessor: (row: PatientDiagnosesReportRow) =>
      row.helper_estimated_comorbidity_adjustment,
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'unacceptable_primary',
    header: 'Unacceptable Primary?',
    accessor: (row: PatientDiagnosesReportRow) =>
      row.unacceptable_primary === null
        ? `Unavailable — ${formatEnumReason(row.unacceptable_primary_reason)}`
        : row.unacceptable_primary
          ? 'Yes'
          : 'No',
    enableSorting: true,
    minWidth: 190,
  },
  {
    id: 'primary_payor',
    header: 'Primary Payor',
    accessor: (row: PatientDiagnosesReportRow) =>
      formatReportEmptyValue(row.primary_payor),
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'primary_payor_type',
    header: 'Primary Payor Type',
    accessor: (row: PatientDiagnosesReportRow) =>
      formatReportEmptyValue(row.primary_payor_type),
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'diagnosis_source_selection_rule',
    header: 'Diagnosis Source',
    accessor: (row: PatientDiagnosesReportRow) =>
      row.diagnosis_source_selection_rule
        ? `${row.diagnosis_source_selection_rule} (${formatReportEmptyValue(row.diagnosis_source_assessment_date)})`
        : 'Unavailable',
    minWidth: 220,
  },
  {
    id: 'hipps_status',
    header: 'HIPPS Basis/Status',
    accessor: (row: PatientDiagnosesReportRow) =>
      row.hipps_status === 'unavailable'
        ? `Unavailable — ${formatEnumReason(row.hipps_unavailable_reason)}`
        : `${row.hipps_basis} / ${row.hipps_status}`,
    minWidth: 260,
  },
  {
    id: 'calculated_hipps_code',
    header: 'Calculated HIPPS',
    accessor: (row: PatientDiagnosesReportRow) =>
      formatReportEmptyValue(row.calculated_hipps_code),
    minWidth: 160,
  },
  {
    id: 'pdgm_period_number',
    header: 'HIPPS Period',
    accessor: (row: PatientDiagnosesReportRow) =>
      row.pdgm_period_number
        ? `Period ${row.pdgm_period_number}: ${row.pdgm_period_start_date} to ${row.pdgm_period_end_date}`
        : 'Unavailable',
    minWidth: 240,
  },
];

const PatientDiagnosesReport = () => {
  const { hasPermission } = useStore(
    useShallow(state => ({ hasPermission: state.hasPermission }))
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [clinics, setClinics] = useState<ClinicOption[]>([]);
  const [report, setReport] = useState<PatientDiagnosesReportResult | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const clinicId = searchParams.get('clinic') ?? '';
  const q = searchParams.get('q') ?? '';
  const unacceptable = readChoiceParam(
    searchParams.get('unacceptable'),
    UNACCEPTABLE_FILTER_OPTIONS,
    ''
  ) as UnacceptablePrimaryFilter | '';
  const group = searchParams.get('group') ?? '';
  const comorbidity = searchParams.get('comorbidity') ?? '';
  const payorType = searchParams.get('payor_type') ?? '';
  const page = readPositiveIntegerParam(searchParams.get('page'));
  const pageSize = readChoiceParam(
    searchParams.get('size'),
    REPORT_PAGE_SIZE_OPTIONS,
    25
  );
  const sortBy = (searchParams.get('sort') || undefined) as
    | PatientDiagnosesSortBy
    | undefined;
  const sortOrder = (searchParams.get('dir') || undefined) as
    | SortOrder
    | undefined;

  const query = useMemo(
    () => ({
      clinic_id: clinicId || undefined,
      q: q || undefined,
      unacceptable_primary: unacceptable || undefined,
      pdgm_grouping: group ? [group] : undefined,
      comorbidity_adjustment: comorbidity
        ? ([comorbidity] as HelperEstimatedComorbidityAdjustment[])
        : undefined,
      payor_type: payorType ? [payorType] : undefined,
      sort_by: sortBy,
      sort_order: sortOrder,
      page,
      page_size: pageSize,
    }),
    [
      clinicId,
      q,
      unacceptable,
      group,
      comorbidity,
      payorType,
      sortBy,
      sortOrder,
      page,
      pageSize,
    ]
  );

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) next.set(key, value);
        else next.delete(key);
      });
      if (!('page' in updates)) next.delete('page');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS)) return;
    let ignore = false;
    setIsLoading(true);
    setError(null);
    getPatientDiagnosesReportReportsPatientDiagnosesGet({ query })
      .then(response => {
        if (ignore) return;
        if (response.error || !response.data) {
          console.error('Failed to load patient diagnoses report', response);
          setReport(null);
          setError(
            getBusinessErrorMessage(response) ??
              'Unable to load report. Please try again.'
          );
          return;
        }
        setReport(response.data);
      })
      .catch(error => {
        console.error('Failed to load patient diagnoses report', error);
        if (!ignore) {
          setReport(null);
          setError(
            getBusinessErrorMessage(error) ??
              'Unable to load report. Please try again.'
          );
        }
      })
      .finally(() => {
        if (!ignore) setIsLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [hasPermission, query]);

  useEffect(() => {
    if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS)) return;
    getPatientDiagnosesReportClinicOptionsReportsPatientDiagnosesClinicOptionsGet()
      .then(response => setClinics(response.data?.items ?? []))
      .catch(error => {
        console.error('Failed to load patient diagnoses clinic options', error);
      });
  }, [hasPermission]);

  const sorting: ReportTableSort | null = sortBy
    ? { id: sortBy, direction: sortOrder ?? 'asc' }
    : null;

  const reportsBackHref = useReportsBackHref();
  const shouldShowTable = !error || report !== null;
  const totalPages = Math.max(1, Math.ceil((report?.total ?? 0) / pageSize));

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const { page: _page, page_size: _pageSize, ...exportQuery } = query;
      void _page;
      void _pageSize;
      const response =
        await exportPatientDiagnosesReportReportsPatientDiagnosesExportGet({
          query: exportQuery,
        });
      if (response.error || !response.data) {
        setError(
          await getUserFacingApiMessageAsync(
            response,
            'Unable to export report. Please try again.'
          )
        );
        return;
      }
      await downloadResponseAsFile(
        response as AxiosResponse<Blob | File>,
        `RogerPatientDiagnosesReport_${report?.report_date ?? 'current'}.xlsx`
      );
    } catch (error) {
      console.error('Failed to export patient diagnoses report', error);
      setError(
        (await getUserFacingApiMessageAsync(
          error,
          'Unable to export report. Please try again.'
        )) ?? 'Unable to export report. Please try again.'
      );
    } finally {
      setIsExporting(false);
    }
  };

  if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS))
    return <ReportsPermissionAlert />;

  return (
    <div className='space-y-6'>
      <div className='space-y-2'>
        <Link
          to={reportsBackHref}
          className='inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground'
        >
          <ArrowLeft className='h-4 w-4' /> Reports
        </Link>
        <div className='flex flex-col gap-3 md:flex-row md:items-start md:justify-between'>
          <div>
            <h1 className='text-2xl font-bold'>Patient Diagnoses</h1>
            <p className='text-sm text-muted-foreground'>
              Review active patients by diagnosis, payer, and helper-estimated
              HIPPS details.
            </p>
            {report?.report_date ? (
              <p className='mt-1 text-xs text-muted-foreground'>
                Report date: {report.report_date}
              </p>
            ) : null}
          </div>
          <Button
            variant='outline'
            size='sm'
            onClick={handleExport}
            disabled={isExporting || isLoading}
            className='w-fit'
          >
            <Download className='h-4 w-4' />
            {isExporting ? 'Exporting…' : 'Export'}
          </Button>
        </div>
      </div>

      <FilterToolbar
        search={{
          value: q,
          onChange: value => updateParams({ q: value || null }),
          label: 'Search',
          placeholder: 'Search patient, MRN, or diagnosis...',
          'aria-label': 'Search patient diagnoses',
        }}
        reset={{
          label: 'Reset',
          onClick: () =>
            setSearchParams(new URLSearchParams(), { replace: true }),
        }}
      >
        <label className='flex flex-col gap-1 text-sm'>
          Clinic
          <Select
            value={clinicId || ALL_VALUE}
            onValueChange={value =>
              updateParams({ clinic: value === ALL_VALUE ? null : value })
            }
          >
            <SelectTrigger selectSize='default'>
              <SelectValue placeholder='All clinics' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>All clinics</SelectItem>
              {clinics.map(clinic => (
                <SelectItem key={clinic.id} value={clinic.id}>
                  {clinic.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label className='flex flex-col gap-1 text-sm'>
          Unacceptable
          <Select
            value={unacceptable || ALL_VALUE}
            onValueChange={value =>
              updateParams({ unacceptable: value === ALL_VALUE ? null : value })
            }
          >
            <SelectTrigger selectSize='default'>
              <SelectValue placeholder='All' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>All</SelectItem>
              <SelectItem value='yes'>Yes</SelectItem>
              <SelectItem value='no'>No</SelectItem>
              <SelectItem value='unknown'>Unknown</SelectItem>
            </SelectContent>
          </Select>
        </label>
        <label className='flex flex-col gap-1 text-sm'>
          PDGM Group
          <Select
            value={group || ALL_VALUE}
            onValueChange={value =>
              updateParams({ group: value === ALL_VALUE ? null : value })
            }
          >
            <SelectTrigger selectSize='default'>
              <SelectValue placeholder='All' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>All</SelectItem>
              {'ABCDEFGHIJKL'.split('').map(value => (
                <SelectItem key={value} value={value}>
                  {value}
                </SelectItem>
              ))}
              <SelectItem value='unavailable'>Unavailable</SelectItem>
            </SelectContent>
          </Select>
        </label>
        <label className='flex flex-col gap-1 text-sm'>
          Comorbidity
          <Select
            value={comorbidity || ALL_VALUE}
            onValueChange={value =>
              updateParams({ comorbidity: value === ALL_VALUE ? null : value })
            }
          >
            <SelectTrigger selectSize='default'>
              <SelectValue placeholder='All' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>All</SelectItem>
              <SelectItem value='none'>None</SelectItem>
              <SelectItem value='low'>Low</SelectItem>
              <SelectItem value='high'>High</SelectItem>
              <SelectItem value='unavailable'>Unavailable</SelectItem>
            </SelectContent>
          </Select>
        </label>
        <label className='flex flex-col gap-1 text-sm'>
          Payor Type
          <Select
            value={payorType || ALL_VALUE}
            onValueChange={value =>
              updateParams({ payor_type: value === ALL_VALUE ? null : value })
            }
          >
            <SelectTrigger selectSize='default'>
              <SelectValue placeholder='All' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>All</SelectItem>
              {PAYOR_TYPE_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      </FilterToolbar>

      {error ? (
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800'>{error}</AlertDescription>
        </Alert>
      ) : null}

      {shouldShowTable ? (
        <ReportTable<PatientDiagnosesReportRow>
          caption='Patient diagnoses report results'
          columns={columns}
          data={report?.items ?? []}
          getRowId={row => row.episode_id}
          isLoading={isLoading}
          sorting={sorting}
          onSortingChange={next =>
            updateParams({
              sort: next?.id ?? null,
              dir: next?.direction ?? null,
            })
          }
          pagination={{
            page,
            pageSize,
            total: report?.total ?? 0,
            totalPages,
            pageSizeOptions: REPORT_PAGE_SIZE_OPTIONS,
          }}
          onPaginationChange={(next: ReportTablePaginationState) =>
            updateParams({
              page: String(next.page),
              size: String(next.pageSize),
            })
          }
          emptyState={
            <EmptyState
              title='No diagnoses found'
              description='Adjust your filters and try again.'
              action={
                <Button
                  onClick={() =>
                    setSearchParams(new URLSearchParams(), { replace: true })
                  }
                >
                  Reset
                </Button>
              }
            />
          }
        />
      ) : null}
    </div>
  );
};

export default PatientDiagnosesReport;
